import { useMemo, useState } from 'react';

import {
  CampaignStoreItem,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { Constants } from 'core';
import { ContactOrganizerModal, EndedCampaignAlert, ShareModal } from 'core/Components';
import { useCauseParam, useCrowdFundingTranslations, usePlatformShare } from 'core/Hooks';
import { BASE_URL } from 'core/Utils';
import { Navigate, useNavigate } from 'react-router-dom';
import { RichTextDisplay } from 'rich-text';
import {
  Box,
  ButtonMantine,
  Container,
  FullWidthButtonGroup,
  Group,
  KebabMenu,
  MobileBottomButton,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';
import { IconShare } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../../Context';
import { useSelectStoreItemProvider } from '../../../../components';
import {
  CampaignProgressWithStats,
  CampaignStatsCard,
  StudentCenterButton,
} from '../../../components';
import { CampaignDonationConfirmation } from '../../../components/CampaignDonationConfirmation';
import classes from '../../../components/CausePageLayout.module.css';
import { HeaderImage } from '../../../components/HeaderImage';
import { Benefits } from './Benefits';
import { RecentCampaignDonations } from './RecentCampaignDonations';

// const CampaignHelmet = ({ campaign }: { campaign?: CampaignStoreItem }) => {
//   return null;
// };

const CampaignShareButton = ({
  campaign,
  causeId,
}: {
  campaign?: CampaignStoreItem;
  causeId?: string;
}) => {
  const { data: cause } = useCauseParam();

  const [isModalOpen, setIsModalOpen] = useState<'share' | 'contact' | undefined>();

  const { platformShare } = usePlatformShare();
  const navigate = useNavigate();
  if (!campaign || !causeId) return null;

  const campaignPageLink = `${BASE_URL}${Constants.appRoutes.campaign(
    cause?.shortener ?? causeId,
    campaign?.shortener ?? campaign?.uuid
  )}`;

  return (
    <>
      <Group gap="xs">
        <ButtonMantine
          size="sm"
          variant="white"
          color="gray"
          leftSection={<IconShare />}
          onClick={() =>
            void platformShare(
              {
                share_options: { url: campaignPageLink },
              },
              () => setIsModalOpen('share')
            )
          }
        >
          Share
        </ButtonMantine>
        <KebabMenu
          items={[
            { label: 'Contact Organizer', onClick: () => setIsModalOpen('contact') },
            {
              label: 'Visit Cause Page',
              onClick: () => navigate(`/causes/${cause?.uuid}`),
            },
            { label: 'Share Campaign Page', onClick: () => setIsModalOpen('share') },
          ]}
          buttonProps={{ variant: 'white', color: 'gray', size: 'sm' }}
        />
      </Group>

      <ShareModal
        opened={isModalOpen === 'share'}
        onClose={() => setIsModalOpen(undefined)}
        causeName={cause?.name}
        link={campaignPageLink}
        type="campaign"
        imgSrc={cause?.logo?.url}
      />
      <ContactOrganizerModal
        opened={isModalOpen === 'contact'}
        onClose={() => setIsModalOpen(undefined)}
        title="Contact Organizer"
        causeId={cause?.uuid}
        campaignId={campaign?.uuid}
      />
    </>
  );
};

const Campaign = () => {
  const { campaignId, causeId } = useCrowdfundingProvider();

  const {
    data: campaign,
    isLoading,
    isError,
  } = useShowConsumerCampaignById({ campaignId, causeId });
  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId,
    campaignId,
  });
  const { t } = useCrowdFundingTranslations(campaign?.cause_id);
  const navigate = useNavigate();
  const { openModalById } = useSelectStoreItemProvider({});

  const aboutCampaign = useMemo(() => {
    return (
      campaign?.description && (
        <Skeleton visible={isLoading}>
          <Stack>
            <Title order={2}>About Our Campaign</Title>

            <Text c="gray.8" type="body" style={{ whiteSpace: 'pre-wrap' }}>
              <RichTextDisplay html={campaign?.description} />
            </Text>
          </Stack>
        </Skeleton>
      )
    );
  }, [campaign, isLoading]);

  const customSections = useMemo(() => {
    return campaign?.sections?.map((section, index) => (
      <Stack key={index}>
        <Title order={2}>{section.title}</Title>
        <Text c="gray.8" type="body" style={{ whiteSpace: 'pre-wrap' }}>
          <RichTextDisplay html={section?.description} />
        </Text>
      </Stack>
    ));
  }, [campaign?.sections]);

  if (isError) {
    return <Navigate to={'..'} />;
  }

  if (!campaign) {
    return null;
  }

  return (
    <Container
      fluid
      className={classes.container}
      style={{
        // Set Mobile Button Bottom Height via local container style
        '--mobile-bottom-button-height': '80px',
      }}
    >
      {/* <CampaignHelmet /> */}
      <Stack className={classes.leftContent}>
        <HeaderImage
          isLoading={isLoading}
          src={
            campaign?.images?.[0]?.url ||
            'https://s3.amazonaws.com/com.givebacks.public/images/crowdfunding/campaign-default.png'
          }
          headerButtons={<CampaignShareButton campaign={campaign} causeId={causeId} />}
        />
        <Skeleton visible={isLoading}>
          <Title order={1} c="gray.8">
            {campaign?.name}
          </Title>
        </Skeleton>
        <Box className={classes.mobileRightContent}>
          <Stack gap="lg">
            <Stack>
              <CampaignDonationConfirmation />
              <CampaignProgressWithStats />
            </Stack>
            {campaignSettings?.isCampaignReadOnly && <EndedCampaignAlert type="campaign" />}
          </Stack>
        </Box>
        {campaignSettings?.isPeersEnabled && <StudentCenterButton />}
        {aboutCampaign}
        {customSections}
        {campaign.benefit_options?.enabled && (
          <Benefits
            campaignId={campaign.uuid}
            causeId={campaign.cause_id}
            benefitsTitle={campaign.benefit_options?.title}
          />
        )}
        {campaign?.uuid && <RecentCampaignDonations campaign_uuid={campaign?.uuid} />}
      </Stack>
      <Box className={classes.rightContent}>
        <Stack className={classes.rightContentChild}>
          <CampaignDonationConfirmation />
          <CampaignStatsCard />
        </Stack>
      </Box>
      {!campaignSettings?.isCampaignReadOnly && (
        <MobileBottomButton>
          <FullWidthButtonGroup>
            <ButtonMantine
              variant="gradient"
              onClick={() => openModalById('StoreItemDonateOptions')}
            >
              {t('Donate')}
            </ButtonMantine>
            {campaignSettings?.isRegistrationOpen && (
              <ButtonMantine onClick={() => navigate('participant-center/register')}>
                {campaign?.participant_options.registration_call_to_action}
              </ButtonMantine>
            )}
          </FullWidthButtonGroup>
        </MobileBottomButton>
      )}
    </Container>
  );
};
const CampaignPage = () => {
  return <Campaign />;
};
export { CampaignPage };
